"use client"

// useBundleMatrixData — hook for the Bundle × SKU Matrix endpoint.
//
// Mirrors useBundlePerformance: module-level ResponseCache (5-min TTL),
// auth-scoped cache key (user + active tenant), stale-response guard via
// requestSeqRef, refresh() for forced invalidation.

import { useCallback, useEffect, useRef, useState } from "react"
import { useAuth } from "@/hooks/use-auth"
import type {
  BundleSkuMatrixResponse,
  MatrixGroupBy,
} from "@/lib/shopify/bundles/types"
import { ResponseCache, useResponseCacheGeneration } from "./_response-cache"

export interface UseBundleMatrixDataOptions {
  integrationId: string
  /** Inclusive ISO-8601. */
  periodStart: string
  /** Exclusive ISO-8601. */
  periodEnd: string
  groupBy: MatrixGroupBy
  /**
   * Picked bundle_definition_ids. Order matters for column ordering on the
   * server response. Empty array short-circuits the fetch to an empty
   * response (still calls server so the standalone column is correct
   * partition-wise — server respects the empty list).
   */
  bundleDefinitionIds: ReadonlyArray<string>
  minimumOrderTotal?: number
  freeShippingThreshold?: number
  includeHypothetical?: boolean
  /**
   * When false, the hook idles — no auto-fetch. Flip to true to trigger
   * one (e.g. when the user expands a family row in Family mode and we
   * lazily load the SKU-mode matrix to derive child rows). Default true
   * keeps the primary matrix-mode hook firing unconditionally.
   */
  enabled?: boolean
}

export interface UseBundleMatrixDataResult {
  data: BundleSkuMatrixResponse | null
  isLoading: boolean
  error: string | null
  refresh: () => Promise<void>
}

const CACHE_TTL_MS = 5 * 60 * 1000
const cache = new ResponseCache<BundleSkuMatrixResponse>(CACHE_TTL_MS)

function cacheKey(opts: {
  userId: string
  activeTenantId: string
  integrationId: string
  periodStart: string
  periodEnd: string
  groupBy: MatrixGroupBy
  bundleIds: ReadonlyArray<string>
  minimumOrderTotal: number
  freeShippingThreshold: number
  includeHypothetical: boolean
}): string {
  return [
    opts.userId,
    opts.activeTenantId,
    opts.integrationId,
    opts.periodStart,
    opts.periodEnd,
    opts.groupBy,
    opts.bundleIds.join(","),
    opts.minimumOrderTotal,
    opts.freeShippingThreshold,
    opts.includeHypothetical ? "h1" : "h0",
  ].join("|")
}

export function useBundleMatrixData({
  integrationId,
  periodStart,
  periodEnd,
  groupBy,
  bundleDefinitionIds,
  minimumOrderTotal = 0,
  freeShippingThreshold = 0,
  includeHypothetical = false,
  enabled = true,
}: UseBundleMatrixDataOptions): UseBundleMatrixDataResult {
  const { fetchWithAuth, isAuthenticated, user } = useAuth()
  const cacheGeneration = useResponseCacheGeneration()
  const userId = user?.user_id ?? ""
  const activeTenantId = user?.impersonated_tenant_id ?? user?.tenant_id ?? ""

  const [data, setData] = useState<BundleSkuMatrixResponse | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Stable identity for the picker selection — joined string is enough,
  // useMemo would just duplicate the work the cache key already does.
  const bundleIdsKey = bundleDefinitionIds.join(",")

  const requestSeqRef = useRef(0)

  useEffect(() => {
    if (!isAuthenticated) cache.clear()
  }, [isAuthenticated])

  const fetchData = useCallback(
    async (useCache: boolean) => {
      const localSeq = ++requestSeqRef.current

      if (
        !integrationId ||
        !isAuthenticated ||
        !periodStart ||
        !periodEnd ||
        !userId ||
        !activeTenantId
      ) {
        if (localSeq === requestSeqRef.current) {
          setData(null)
          setError(null)
          setIsLoading(false)
        }
        return
      }

      const ids = bundleIdsKey.length > 0 ? bundleIdsKey.split(",") : []
      const key = cacheKey({
        userId,
        activeTenantId,
        integrationId,
        periodStart,
        periodEnd,
        groupBy,
        bundleIds: ids,
        minimumOrderTotal,
        freeShippingThreshold,
        includeHypothetical,
      })

      if (useCache) {
        const cached = cache.get(key)
        if (cached != null) {
          if (localSeq === requestSeqRef.current) {
            setData(cached)
            setError(null)
            setIsLoading(false)
          }
          return
        }
      }

      setIsLoading(true)
      setError(null)

      try {
        const params = new URLSearchParams({
          integration_id: integrationId,
          period_start: periodStart,
          period_end: periodEnd,
          group_by: groupBy,
          minimum_order_total: String(minimumOrderTotal),
          free_shipping_threshold: String(freeShippingThreshold),
        })
        if (ids.length > 0) params.set("bundle_definition_ids", ids.join(","))
        if (includeHypothetical) params.set("include_hypothetical", "true")

        const res = await fetchWithAuth(
          `/api/integrations/shopify/bundle-sku-matrix?${params}`,
        )
        if (!res.ok) {
          const body = await res.json().catch(() => ({}))
          throw new Error(body.error || `HTTP ${res.status}`)
        }
        const payload: BundleSkuMatrixResponse = await res.json()
        cache.set(key, payload, cacheGeneration)
        if (localSeq === requestSeqRef.current) {
          setData(payload)
        }
      } catch (err: unknown) {
        if (localSeq === requestSeqRef.current) {
          const message = err instanceof Error ? err.message : "Failed to load bundle matrix"
          setError(message)
          setData(null)
        }
      } finally {
        if (localSeq === requestSeqRef.current) setIsLoading(false)
      }
    },
    [
      cacheGeneration,
      integrationId,
      isAuthenticated,
      userId,
      activeTenantId,
      periodStart,
      periodEnd,
      groupBy,
      bundleIdsKey,
      minimumOrderTotal,
      freeShippingThreshold,
      includeHypothetical,
      fetchWithAuth,
    ],
  )

  const refresh = useCallback(async () => {
    if (integrationId && periodStart && periodEnd && userId && activeTenantId) {
      const ids = bundleIdsKey.length > 0 ? bundleIdsKey.split(",") : []
      cache.delete(
        cacheKey({
          userId,
          activeTenantId,
          integrationId,
          periodStart,
          periodEnd,
          groupBy,
          bundleIds: ids,
          minimumOrderTotal,
          freeShippingThreshold,
          includeHypothetical,
        }),
      )
    }
    await fetchData(false)
  }, [
    fetchData,
    userId,
    activeTenantId,
    integrationId,
    periodStart,
    periodEnd,
    groupBy,
    bundleIdsKey,
    minimumOrderTotal,
    freeShippingThreshold,
    includeHypothetical,
  ])

  useEffect(() => {
    if (!enabled) {
      // Lazy use case (e.g. Family-mode inline expansion's secondary
      // fetch): when the consumer flips `enabled` off (all families
      // collapsed), clear cached state so a later re-enable can't
      // briefly render the previous response while a request for the
      // current params is in flight. Primary callers leave `enabled`
      // at its default `true` and never hit this branch — behaviour
      // there is unchanged.
      requestSeqRef.current += 1
      setData(null)
      setError(null)
      setIsLoading(false)
      return
    }
    void fetchData(true)
  }, [enabled, fetchData])

  return { data, isLoading, error, refresh }
}
